// Damage with a type, and what stops it (docs/MRCS/combat-rules.md, "Damage types and what stops them"). Every word here
// is the system's, from new optional keys in `features.combat.damage`: a system that declares none has no step at all,
// and a creature whose block cannot be read takes what it takes today.
//
// `readStops` reads a creature's stat block in the data's own shapes: the `defences` register's structured value
// (docs/MRCS/monster-attacks.md) and the text fields the system names. The result is stamped on the record as the attack
// is made, and `applyStops` works from the stamp, so a replay reads the same. The stamp is the DM's: `playerAttack` is a
// whitelist and only the attack's rolled damage goes through it, unless the DM told the players (`told`).
export const STOP_LIMITS = { text: 200, words: 16, word: 40, list: 12, amount: 1000 };
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, lo, hi) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const isWord = (v) => typeof v === 'string' && /^[a-z][a-z' _-]{0,39}$/.test(v);
const isLabel = (v) => typeof v === 'string' && v.trim().length >= 1 && v.length <= 40;
const words = (v) => (v === undefined ? [] : Array.isArray(v) && v.length <= 64 && v.every(isWord) ? [...v] : null);
const stats = (v) => (v === undefined ? [] : typeof v === 'string' ? [v] : Array.isArray(v) && v.length <= 4 && v.every((k) => typeof k === 'string') ? [...v] : null);
function adjust(v) {
    if (v === undefined)
        return undefined;
    if (!isObj(v) || !isLabel(v.label))
        return null;
    const s = stats(v.stat);
    const w = words(v.words);
    if (!s || !w || (v.times !== undefined && !(typeof v.times === 'number' && v.times >= 0 && v.times <= 10)))
        return null;
    return { label: v.label.trim(), stats: s, words: w, times: typeof v.times === 'number' ? v.times : null };
}
/** The optional keys of `damage`: null when it names none of them, or one this parser does not accept. */
export function parseDamageRules(d) {
    if (!isObj(d) || !['types', 'attack_types', 'reduction', 'adjust', 'temporary', 'weapon_stat', 'spell_stat', 'spell_attr'].some((k) => d[k] !== undefined))
        return null;
    const types = [];
    if (d.types !== undefined) {
        if (!Array.isArray(d.types) || d.types.length > 64)
            return null;
        for (const t of d.types) {
            if (!isObj(t) || !isWord(t.key) || !isLabel(t.label) || (t.physical !== undefined && typeof t.physical !== 'boolean'))
                return null;
            types.push({ key: t.key, label: t.label.trim(), physical: t.physical === true });
        }
    }
    const attackTypes = [];
    if (d.attack_types !== undefined) {
        if (!Array.isArray(d.attack_types) || d.attack_types.length > 64)
            return null;
        for (const a of d.attack_types) {
            const ts = isObj(a) ? words(a.types) : null;
            if (!isObj(a) || !isWord(a.word) || !ts?.length || !ts.every((k) => types.some((t) => t.key === k)))
                return null;
            attackTypes.push({ word: a.word, types: ts });
        }
    }
    let reduction = null;
    if (d.reduction !== undefined) {
        const r = d.reduction;
        if (!isObj(r) || !isLabel(r.label))
            return null;
        const s = stats(r.stat);
        const materials = words(r.materials);
        if (!s || !materials)
            return null;
        const enhancement = [];
        if (r.enhancement !== undefined) {
            if (!Array.isArray(r.enhancement) || r.enhancement.length > 8)
                return null;
            for (const e of r.enhancement) {
                const w = isObj(e) ? words(e.words) : null;
                if (!isObj(e) || !isInt(e.at, 1, 100) || !w?.length)
                    return null;
                enhancement.push({ at: e.at, words: w });
            }
        }
        reduction = { label: r.label.trim(), stats: s, materials, enhancement };
    }
    const a = isObj(d.adjust) ? d.adjust : {};
    if (d.adjust !== undefined && !isObj(d.adjust))
        return null;
    const [resistance, immunity, vulnerability] = [adjust(a.resistance), adjust(a.immunity), adjust(a.vulnerability)];
    if (resistance === null || immunity === null || vulnerability === null)
        return null;
    if (d.temporary !== undefined && typeof d.temporary !== 'boolean')
        return null;
    if (d.weapon_stat !== undefined && typeof d.weapon_stat !== 'string')
        return null;
    if (d.spell_stat !== undefined && typeof d.spell_stat !== 'string')
        return null;
    if (d.spell_attr !== undefined && typeof d.spell_attr !== 'string')
        return null;
    return { types, attackTypes, reduction, resistance: resistance ?? null, immunity: immunity ?? null, vulnerability: vulnerability ?? null, temporary: d.temporary === true, weaponStat: typeof d.weapon_stat === 'string' ? d.weapon_stat : null, spellStat: typeof d.spell_stat === 'string' ? d.spell_stat : null, spellAttr: typeof d.spell_attr === 'string' ? d.spell_attr : null };
}
export const stopRulesOf = (r) => ({
    physical: r.types.filter((t) => t.physical).map((t) => t.key),
    reduction: r.reduction?.label ?? null,
    resistance: r.resistance ? { label: r.resistance.label, times: r.resistance.times } : null,
    immunity: r.immunity?.label ?? null,
    vulnerability: r.vulnerability ? { label: r.vulnerability.label, times: r.vulnerability.times ?? 1 } : null,
});
/** Whether a system's rules have anything that stops damage. */
export const stopsDamage = (r) => !!r && !!(r.reduction || r.resistance || r.immunity || r.vulnerability);
// ── A weapon's types and what it counts as ───────────────────────────────
const typeOf = (r, word) => r.types.find((x) => x.key === word || x.label.toLowerCase() === word)?.key;
const inName = (name, word) => new RegExp(`(^|[^a-z])${word.replace(/[^a-z' -]/g, '')}s?([^a-z]|$)`).test(name);
/** The system's types in some text: "Piercing or slashing", "piercing/slashing", "negative energy". */
export function typesIn(r, text) {
    const t = text.toLowerCase();
    return r.types.filter((x) => inName(t, x.label.toLowerCase())).map((x) => x.key);
}
/** The types of an attack's first dice: those its label names, else those a word of its name carries. */
export function attackTypesOf(r, label, name) {
    const said = label ? typesIn(r, label) : [];
    if (said.length)
        return said;
    const n = name.toLowerCase();
    return r.attackTypes.find((a) => inName(n, a.word))?.types ?? [];
}
/** What a weapon counts as against a bypass, from what the table knows: its types, its name's materials, its "+1". */
export function weaponWords(r, name, types) {
    const n = name.toLowerCase();
    const out = new Set(types);
    for (const m of r.reduction?.materials ?? [])
        if (inName(n, m))
            out.add(m);
    const plus = /\+(\d+)/.exec(n);
    if (plus)
        for (const e of r.reduction?.enhancement ?? [])
            if (Number(plus[1]) >= e.at)
                for (const w of e.words)
                    out.add(w);
    return [...out];
}
const plainName = (s) => s.toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z', ]+/g, ' ').replace(/\s+/g, ' ').trim();
/** "Sword, short" also reads "short sword". */
const namesOf = (name) => { const n = plainName(name); return n.includes(',') ? [n, n.split(/\s*,\s*/).reverse().join(' ')] : [n]; };
/** The word to look an attack's weapon up by: its name's last, one of many as one ("short swords": "sword"). */
export const weaponSearchWord = (name) => /([a-z']{3,})$/.exec(plainName(name))?.[1]?.replace(/(?<=[^s])s$/, '') ?? null;
/** Of named entries, the one an attack's name holds ("+1 vorpal longsword", "mwk short sword"): the longest name that fits. */
export function weaponFit(name, entries) {
    const n = plainName(name);
    // "short sword" for an item called "Shortsword": each pair of its words closed up too.
    const parts = n.split(' ');
    const forms = [n, ...parts.slice(1).map((_, i) => [...parts.slice(0, i), parts[i] + parts[i + 1], ...parts.slice(i + 2)].join(' '))];
    let best = null;
    let size = 0;
    for (const w of entries)
        for (const said of namesOf(w.name)) {
            if (said.length > size && said.length >= 3 && forms.some((f) => inName(f, said))) {
                best = w;
                size = said.length;
            }
        }
    return best;
}
/** Damage words with its types said: "1d8+3" is "1d8+3 slashing". Unchanged when they cannot be, or the words say one already. */
export function sayTypes(damage, types) {
    const head = /^\s*\d+\s*d\s*\d+(?:\s*[+-]\s*\d+)?/i.exec(damage);
    if (!head || !types.length || /^\s*[a-z]/i.test(damage.slice(head[0].length).replace(/^\s*plus\b.*$/i, '')))
        return damage;
    return `${head[0]} ${types.join('/')}${damage.slice(head[0].length)}`;
}
/** Whether an attack's name needs a weapon's types looked up: a natural attack's word, or the block's own, says them first. */
export const namesAWeapon = (r, a) => !!a.damage && !attackTypesOf(r, undefined, a.name).length && sayTypes(a.damage, ['x']) !== a.damage;
/** A stat block's attacks with each weapon's types said, as `types` holds them by attack name. */
export function withWeaponTypes(r, attacks, types) {
    return attacks.map((a) => {
        const said = a.damage && namesAWeapon(r, a) ? sayTypes(a.damage, types.get(a.name) ?? []) : a.damage;
        return said === a.damage ? a : { ...a, damage: said };
    });
}
/**
 * The damage type a spell's links name: the first that is one of the system's types and not a weapon's ("fire", from
 * "evil" and "fire"), in the words a damage text takes. A link of two ("fire-or-cold") gives the one it says first.
 */
export function spellDamageType(r, links) {
    for (const link of links) {
        const said = link.toLowerCase().replace(/[-_]+/g, ' ');
        const found = r.types.filter((t) => !t.physical && inName(said, t.label.toLowerCase())).sort((a, b) => said.indexOf(a.label.toLowerCase()) - said.indexOf(b.label.toLowerCase()))[0];
        if (found)
            return found.label.toLowerCase();
    }
    return null;
}
// ── What a creature has, from what the data holds ────────────────────────
const clean = (s) => s.toLowerCase().replace(/&[mn]dash;?/g, '-').replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
/** "10/evil or silver", "5/-", "10/cold iron and magic": a list of them, in any of the shapes the data prints. */
function reductionsIn(text) {
    const out = [];
    for (const m of clean(text).split(';').flatMap((p) => [...p.matchAll(/(\d+)\s*\/\s*([^/]*?)(?=\s*,?\s*\d+\s*\/|$)/g)])) {
        const amount = Number(m[1]);
        const said = m[2].replace(/[;,.]+\s*$/, '').trim();
        if (!amount || amount > STOP_LIMITS.amount)
            continue;
        if (/^[-–—]*$/.test(said)) {
            out.push({ amount, bypass: [] });
            continue;
        }
        const join = /\band\b/.test(said) && !/\bor\b/.test(said) ? 'and' : 'or';
        const bypass = said.split(/\s+(?:and|or)\s+|,\s*(?:and\s+|or\s+)?/).map((w) => w.trim()).filter(Boolean);
        if (!bypass.length || !bypass.every(isWord))
            continue;
        out.push({ amount, bypass, ...(bypass.length > 1 ? { join } : {}) });
    }
    return out.slice(0, STOP_LIMITS.list);
}
/** "acid 10, cold 10": each of the system's types with its number. A word that is not a type ("channel 4") is left out. */
function resistancesIn(r, text) {
    const out = [];
    for (const m of clean(text).matchAll(/([a-z][a-z ]*?)\s+\+?(\d+)/g)) {
        const type = typesIn(r, m[1].replace(/^.*\b(?:and|to)\s+/, ''))[0];
        const amount = Number(m[2]);
        if (type && amount >= 1 && amount <= STOP_LIMITS.amount && !out.some((x) => x.type === type))
            out.push({ type, amount });
    }
    return out.slice(0, STOP_LIMITS.list);
}
/** The types after one of `lead` in prose ("vulnerability to fire, deaf"); with no `lead`, the types in a whole list field. */
function typesAfter(r, text, lead) {
    const t = clean(text);
    if (!lead.length)
        return t.split(/[,;]|\band\b/).flatMap((w) => { const k = typeOf(r, w.trim()); return k ? [k] : []; });
    const out = new Set();
    for (const w of lead)
        for (const m of t.matchAll(new RegExp(`${w.replace(/[^a-z' -]/g, '')}\\s+([^;.]*)`, 'g'))) {
            for (const piece of m[1].split(/,|\band\b/)) {
                const k = typeOf(r, piece.trim());
                if (k)
                    out.add(k);
                else
                    break;
            }
        }
    return [...out];
}
const some = (c) => (c.reduction?.length || c.resistance?.length || c.immunity?.length || c.vulnerability?.length ? c : null);
/**
 * A creature's stops from its stat block: the `defences` register's value, then the text fields the rules name. What
 * cannot be read is left out; nothing read at all is null, and that creature behaves as if the step were not there.
 */
export function readStops(r, block) {
    const out = {};
    const d = isObj(block.register) ? block.register : null;
    const texts = (ks) => ks.map(block.text).filter((t) => !!t);
    const reduction = Array.isArray(d?.damage_reduction) ? d.damage_reduction.flatMap((e) => {
        if (!isObj(e) || !isInt(e.amount, 1, STOP_LIMITS.amount) || !Array.isArray(e.bypass) || !e.bypass.every(isWord))
            return [];
        return [{ amount: e.amount, bypass: [...e.bypass], ...(e.join === 'and' || e.join === 'or' ? { join: e.join } : {}) }];
    }) : [];
    if (r.reduction)
        reduction.push(...texts(r.reduction.stats).flatMap(reductionsIn));
    if (r.reduction && reduction.length)
        out.reduction = reduction.slice(0, STOP_LIMITS.list);
    const resistance = Array.isArray(d?.energy_resistance) ? d.energy_resistance.flatMap((e) => { const type = isObj(e) && typeof e.type === 'string' ? typeOf(r, e.type) : undefined; return type && isObj(e) && isInt(e.amount, 1, STOP_LIMITS.amount) ? [{ type, amount: e.amount }] : []; }) : [];
    // `damage_resistances` and the two lists beside it (docs/MRCS/dnd-5e.md §3.3): only ever words; the 3.5 keys stay as they are.
    const listed = (k) => (Array.isArray(d?.[k]) ? d[k].flatMap((w) => { const t = typeof w === 'string' ? typeOf(r, w) : undefined; return t ? [t] : []; }) : []);
    const byTimes = r.resistance && r.resistance.times !== null;
    if (r.resistance)
        resistance.push(...listed('damage_resistances').map((type) => ({ type, amount: 1 })));
    if (r.resistance)
        resistance.push(...texts(r.resistance.stats).flatMap((t) => (byTimes ? typesAfter(r, t, r.resistance.words).map((type) => ({ type, amount: 1 })) : resistancesIn(r, t))));
    if (r.resistance && resistance.length)
        out.resistance = resistance.slice(0, STOP_LIMITS.list);
    const immune = Array.isArray(d?.immunities) ? d.immunities.flatMap((w) => { const k = typeof w === 'string' ? typeOf(r, w) : undefined; return k ? [k] : []; }) : [];
    if (r.immunity)
        immune.push(...listed('damage_immunities'), ...texts(r.immunity.stats).flatMap((t) => typesAfter(r, t, r.immunity.words)));
    if (r.immunity && immune.length)
        out.immunity = [...new Set(immune)].slice(0, STOP_LIMITS.list);
    if (r.vulnerability) {
        const v = [...listed('damage_vulnerabilities'), ...texts(r.vulnerability.stats).flatMap((t) => typesAfter(r, t, r.vulnerability.words))];
        if (v.length)
            out.vulnerability = [...new Set(v)].slice(0, STOP_LIMITS.list);
    }
    return some(out);
}
/**
 * The DM's or a sheet's words for a creature: parts split by ";", each "DR 5/magic", "fire 10", "immune cold, acid" or
 * "vulnerable fire", the labels the system's own. A part it cannot read is left out.
 */
export function parseStopsText(r, text) {
    const out = {};
    for (const raw of text.split(/[;\n]/)) {
        const part = raw.trim();
        const low = part.toLowerCase();
        if (!part)
            continue;
        const leadOf = (a) => (a ? [...a.words, a.label.toLowerCase()].find((w) => low.startsWith(w)) : undefined);
        const im = leadOf(r.immunity);
        const vu = leadOf(r.vulnerability);
        if (im !== undefined) {
            const t = typesAfter(r, low.slice(im.length), []);
            if (t.length)
                out.immunity = [...new Set([...(out.immunity ?? []), ...t])];
            continue;
        }
        if (vu !== undefined) {
            const t = typesAfter(r, low.slice(vu.length), []);
            if (t.length)
                out.vulnerability = [...new Set([...(out.vulnerability ?? []), ...t])];
            continue;
        }
        // A resistance that multiplies has no number to read: "Resistance fire, cold", or a bare list.
        if (r.resistance && r.resistance.times !== null) {
            const t = typesAfter(r, low.slice(leadOf(r.resistance)?.length ?? 0), []);
            if (t.length)
                out.resistance = [...(out.resistance ?? []), ...t.map((type) => ({ type, amount: 1 }))];
            continue;
        }
        if (r.reduction && low.includes('/')) {
            const d = reductionsIn(low.replace(/^[^\d]*/, ''));
            if (d.length)
                out.reduction = [...(out.reduction ?? []), ...d];
            continue;
        }
        if (r.resistance) {
            const e = resistancesIn(r, low);
            if (e.length)
                out.resistance = [...(out.resistance ?? []), ...e];
        }
    }
    return some(out);
}
// ── The damage step ──────────────────────────────────────────────────────
/** Whether a weapon gets past one reduction: nothing passes an empty bypass, "and" needs every word, "or" any. */
const passes = (e, overcome) => e.bypass.length > 0 && (e.join === 'and' ? e.bypass.every((w) => overcome.includes(w)) : e.bypass.some((w) => overcome.includes(w)));
export const reductionWords = (e) => `${e.amount}/${e.bypass.length ? e.bypass.join(` ${e.join ?? 'or'} `) : '-'}`;
/**
 * The damage after what stops it. Each piece has its own type: immunity, then the creature's resistance to it, then
 * vulnerability; then the highest reduction the weapon does not get past comes off the pieces that are physical or
 * have no type, once for the hit. Whole numbers, rounding down; never below nothing.
 */
export function applyStops(r, c, pieces, overcome) {
    const lines = [];
    const out = pieces.map((p) => ({ ...p }));
    const line = (label, before, after) => { if (before !== after)
        lines.push({ label, before, after }); };
    for (const p of out) {
        if (!p.types.length || p.amount <= 0)
            continue;
        const before = p.amount;
        if (r.immunity && p.types.every((t) => c.immunity?.includes(t))) {
            p.amount = 0;
            line(`${r.immunity} ${p.types.join('/')}`, before, 0);
            continue;
        }
        const res = r.resistance ? c.resistance?.filter((e) => p.types.includes(e.type)).sort((a, b) => b.amount - a.amount)[0] : undefined;
        if (res && r.resistance) {
            const was = p.amount;
            p.amount = r.resistance.times === null ? Math.max(0, was - res.amount) : Math.floor(was * r.resistance.times);
            line(`${r.resistance.label} ${res.type}${r.resistance.times === null ? ` ${res.amount}` : ''}`, was, p.amount);
        }
        const weak = r.vulnerability ? p.types.find((t) => c.vulnerability?.includes(t)) : undefined;
        if (weak && r.vulnerability) {
            const was = p.amount;
            p.amount = Math.floor(was * r.vulnerability.times);
            line(`${r.vulnerability.label} ${weak}`, was, p.amount);
        }
    }
    const physical = out.filter((p) => !p.types.length || p.types.some((t) => r.physical.includes(t)));
    const stopping = r.reduction ? (c.reduction ?? []).filter((e) => !passes(e, overcome)).sort((a, b) => b.amount - a.amount)[0] : undefined;
    const hit = physical.reduce((s, p) => s + p.amount, 0);
    if (stopping && hit > 0) {
        let left = stopping.amount;
        for (const p of physical) {
            const take = Math.min(left, p.amount);
            p.amount -= take;
            left -= take;
        }
        line(`${r.reduction} ${reductionWords(stopping)}`, hit, Math.max(0, hit - stopping.amount));
    }
    return { total: Math.max(0, out.reduce((s, p) => s + p.amount, 0)), lines };
}
export const stopLineText = (l) => `${l.label}: ${l.before} → ${l.after}`;
// ── A stamp, from anywhere ───────────────────────────────────────────────
const wordList = (v) => (Array.isArray(v) && v.length <= STOP_LIMITS.words && v.every(isWord) ? [...v] : null);
function parseCreature(v) {
    if (!isObj(v))
        return null;
    const out = {};
    if (v.reduction !== undefined) {
        if (!Array.isArray(v.reduction))
            return null;
        const list = [];
        for (const e of v.reduction) {
            const bypass = isObj(e) ? wordList(e.bypass) : null;
            if (!isObj(e) || !isInt(e.amount, 1, STOP_LIMITS.amount) || !bypass || (e.join !== undefined && e.join !== 'and' && e.join !== 'or'))
                return null;
            list.push({ amount: e.amount, bypass, ...(e.join ? { join: e.join } : {}) });
        }
        out.reduction = list;
    }
    if (v.resistance !== undefined) {
        if (!Array.isArray(v.resistance))
            return null;
        const list = [];
        for (const e of v.resistance) {
            if (!isObj(e) || !isWord(e.type) || !isInt(e.amount, 1, STOP_LIMITS.amount))
                return null;
            list.push({ type: e.type, amount: e.amount });
        }
        out.resistance = list;
    }
    for (const k of ['immunity', 'vulnerability']) {
        if (v[k] === undefined)
            continue;
        const w = wordList(v[k]);
        if (!w)
            return null;
        out[k] = w;
    }
    return out;
}
/** A stored or stamped `stops`: exact shape or null. */
export function parseDamageStops(v) {
    if (!isObj(v))
        return null;
    const creature = parseCreature(v.creature);
    const overcome = wordList(v.overcome);
    const r = v.rules;
    if (!creature || !overcome || !isObj(r))
        return null;
    const physical = wordList(r.physical);
    const label = (x) => (x === null ? null : isLabel(x) ? x : undefined);
    const reduction = label(r.reduction);
    const immunity = label(r.immunity);
    const times = (x) => (x === null || (typeof x === 'number' && x >= 0 && x <= 10));
    const res = r.resistance === null ? null : isObj(r.resistance) && isLabel(r.resistance.label) && times(r.resistance.times) ? { label: r.resistance.label, times: r.resistance.times } : undefined;
    const vul = r.vulnerability === null ? null : isObj(r.vulnerability) && isLabel(r.vulnerability.label) && typeof r.vulnerability.times === 'number' ? { label: r.vulnerability.label, times: r.vulnerability.times } : undefined;
    if (!physical || reduction === undefined || immunity === undefined || res === undefined || vul === undefined)
        return null;
    return { creature, overcome, rules: { physical, reduction, resistance: res, immunity, vulnerability: vul }, ...(v.told === true ? { told: true } : {}) };
}
//# sourceMappingURL=damageStops.js.map